import { SnippetTypeaheadInputProps } from 'components/Wizards/Wizard/Form/Types'
import { SnippetsTextInput } from 'components/Wizards/Wizard/Form/SnippetsTextInput'
import { TypeaheadField } from 'components/Wizards/Wizard/Form/TypeaheadField'
import Styles from './StringInput.module.scss'
import { DynamicGenerateButton } from 'components/Wizards/Field/DynamicGenerateButton'
import { useState, useEffect, useRef } from 'react'
import { Button } from 'blaze-ui/Button'
import { ArrowRight } from 'blaze-ui/icons'
import { GenerateButton } from 'components/Wizards/Field/GenerateButton'
import { createTypeAheadOptions } from 'hooks/queries/aiTypeahead/useCreateAITypeaheadOptions'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useField } from 'formik'
import { debounce } from 'lodash'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

const validSuggestions = (suggestions: Array<string>, text: string) =>
  suggestions.filter((suggestion) => {
    return text.length > 5 && suggestion.substring(0, text.length).toLowerCase() === text.toLowerCase()
  })

const trimSuggestions = (suggestions: Array<string>, text: string) =>
  validSuggestions(suggestions, text)
    .map((suggestion) => {
      return suggestion.slice(text.length)
    })
    .filter(Boolean)

const createTypeahead = async (
  text: string,
  generationType: string,
  setSuggestions: (suggestions: string[]) => void,
  abortController: AbortController,
) => {
  const response = await createTypeAheadOptions({ text, generationType }, { signal: abortController.signal })

  if (response.type === SUCCESS_RESPONSE_TYPE && response.data.options && response.data.options.length > 0) {
    setSuggestions(trimSuggestions(response.data.options, text))
  }
}

const createTypeaheadForType =
  (generationType: string) =>
  (text: string, setSuggestions: (suggestions: string[]) => void, abortController: AbortController) =>
    createTypeahead(text, generationType, setSuggestions, abortController)

export const SnippetTypeaheadInput = ({
  name,
  schema,
  autoFocus,
  actionOnPress,
  actionDisabled,
  typeaheadDisabled,
  hideSnippets,
  useMoreContext,
  actions,
  variant,
  isLoading: isGenerationLoading = false,
  setWritingOwn,
  onKeyDown,
}: SnippetTypeaheadInputProps) => {
  const {
    meta: { placeholder, rows, snippets, generate, writeMyOwn, generateLabel, generationType },
  } = schema.describe()
  const [field, , helpers] = useField(name)
  const snippetKinds = typeof snippets !== 'boolean' ? snippets : undefined
  const [isLoading, setIsLoading] = useState(false)
  const [suggestions, setSuggestions] = useState<string[]>([])
  const textValue = field.value
  const inputRef = useRef<HTMLInputElement>(textValue)
  const lastTextValueRef = useRef<string>(textValue)
  const abortControllerRef = useRef<AbortController | null>(null)
  const { isMobile } = useResponsiveDesign()
  const createSuggestions = useRef(debounce(createTypeaheadForType(generationType || 'instagram_posts'), 500)).current

  useEffect(() => {
    const hasTextChanged = textValue !== lastTextValueRef.current
    if (!hasTextChanged || typeaheadDisabled) return

    setSuggestions([])
    abortControllerRef.current?.abort()

    if (textValue.length < 5) {
      createSuggestions.cancel()
    } else {
      const newAbortController = new AbortController()
      abortControllerRef.current = newAbortController
      createSuggestions(textValue, setSuggestions, newAbortController)
    }

    lastTextValueRef.current = textValue
  }, [textValue, createSuggestions, typeaheadDisabled])

  useEffect(
    () => () => {
      createSuggestions.cancel()
      abortControllerRef.current?.abort()
    },
    [createSuggestions],
  )

  if (!generationType) return null

  const Component = useMoreContext ? DynamicGenerateButton : GenerateButton

  const acceptTypeahead = () => {
    if (!suggestions.length) return

    const newText = textValue + suggestions[0]
    helpers.setValue(newText)
    setSuggestions([])
  }

  const cancelTypeahead = () => {
    setSuggestions([])
  }

  return (
    <>
      <TypeaheadField>
        <SnippetsTextInput
          name={name}
          placeholder={placeholder}
          isTextarea={(rows || 1) > 1}
          kinds={snippetKinds}
          hideSnippets={hideSnippets}
          autoFocus={autoFocus}
          isLoading={isLoading || isGenerationLoading}
          aria-label={name}
          rows={rows}
          typeaheadText={!!suggestions.length ? suggestions[0] : undefined}
          acceptTypeahead={acceptTypeahead}
          cancelTypeahead={cancelTypeahead}
          variant={variant}
          ref={inputRef}
          onKeyDown={onKeyDown}
        />
      </TypeaheadField>
      {name && generate && (
        <div className={Styles.actions}>
          {generate && !writeMyOwn && (
            <div>
              <Component
                field={name}
                onPress={() => setIsLoading(true)}
                onSettled={() => setIsLoading(false)}
                label={generateLabel}
                size={isMobile ? 'md' : 'sm'}
                isDisabled={isLoading || isGenerationLoading}
              />
            </div>
          )}
          {writeMyOwn && (
            <div>
              <Button
                variant="secondary"
                size="sm"
                isDisabled={isLoading || isGenerationLoading}
                onPress={() => {
                  helpers.setValue('')
                  setSuggestions([])
                  inputRef.current.focus()
                  setWritingOwn?.(true)
                }}
              >
                I'll write my own
              </Button>
            </div>
          )}
          {actions}
          {actionOnPress && (
            <Button
              endIcon={ArrowRight}
              isDisabled={actionDisabled || isLoading || isGenerationLoading}
              onPress={actionOnPress}
            >
              Next
            </Button>
          )}
        </div>
      )}
    </>
  )
}
